import { useCallback, useEffect, useId, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { ISSOLaunchResponse } from "../../types/e-prescribe.types";
import {
    useSyncPracticeLevelPrescriptions,
    useTriggerSSOLaunch,
} from "../../hooks/e-prescribe.mutations";
import { useAppSelector } from "../../../../hooks/useRedux";
import useToast from "../../../../hooks/useToast";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";

export default function PracticePrescriptionNotificationsView() {
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );
    const [launchError, setLaunchError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const hasSubmittedRef = useRef(false);
    const hasTriggeredLaunchRef = useRef(false);

    const { practice } = useAppSelector((state) => state.userPractice);
    const practiceId = practice?.practice_id ?? "";

    const triggerSSOLaunch = useTriggerSSOLaunch();
    const { mutate: syncPracticePrescriptions } =
        useSyncPracticeLevelPrescriptions(practiceId);

    const iframeName = useId().replace(/:/g, "");

    const { toast } = useToast();

    const handleTriggerSSOLaunch = useCallback(async () => {
        setLaunchError(null);

        try {
            const res = await triggerSSOLaunch.mutateAsync({
                practiceId,
                payload: {
                    landing_screen: "message",
                    allow_login_without_patient_context: true,
                },
            });

            setLaunchData(res.data);
        } catch (err) {
            const errorMessage = isAxiosError(err)
                ? err.response?.data?.message
                : undefined;
            const fallbackMessage = "Cannot load notifications at this time";
            const message = errorMessage ?? fallbackMessage;

            setLaunchError(message);

            toast({
                mode: "error",
                message,
            });
        }
    }, [practiceId, toast, triggerSSOLaunch]);

    const submitLaunchForm = useCallback(() => {
        if (
            hasSubmittedRef.current ||
            !launchData ||
            !formRef.current ||
            !iframeRef.current
        ) {
            return;
        }

        hasSubmittedRef.current = true;

        formRef.current.requestSubmit();
    }, [launchData]);

    useEffect(() => {
        if (!practiceId || hasTriggeredLaunchRef.current) {
            return;
        }

        hasTriggeredLaunchRef.current = true;
        handleTriggerSSOLaunch();
    }, [handleTriggerSSOLaunch, practiceId]);

    useEffect(() => {
        return () => {
            syncPracticePrescriptions();
        };
    }, [syncPracticePrescriptions]);

    if (launchError) {
        return (
            <ListState
                isError
                stateHelperText="Try reloading this page to solve this issue"
                errorMsg={launchError}
            />
        );
    }

    if (triggerSSOLaunch.isLoading && !launchData) {
        return <Loader />;
    }

    if (!launchData) {
        return null;
    }

    return (
        <div className="h-[90vh] w-full">
            <form
                ref={(node) => {
                    formRef.current = node;
                    submitLaunchForm();
                }}
                action={launchData.landingUrl}
                method="post"
                target={iframeName}
                className="hidden"
            >
                <input type="hidden" name="token" value={launchData.token} />
            </form>

            <iframe
                ref={(node) => {
                    iframeRef.current = node;
                    submitLaunchForm();
                }}
                name={iframeName}
                id={iframeName}
                title="Notifications"
                width="100%"
                height="100%"
                className="border-0"
            />
        </div>
    );
}
